import { observer } from 'mobx-react-lite'
import { ComponentProps, FC } from 'react'
import { IArpMedia } from '../../../features/arp-registry/ArpMedia'
import { useAuth } from '../../hooks/useAuth'
import useFavorite from '../../hooks/useFavorite'
import { requiresUserLoggedIn } from '../AuthFlow/AuthFlowModal'
import { userIsLoggedIn } from './Authentication'
import { IconButton } from './Components'
import { HeartFilled, HeartOutlined } from './icons'
import { MediaTypesType } from './Utils'
import withTooltip from './withTooltip'

const FavoriteWithTooltip = withTooltip(IconButton)

type FavoriteButtonProps = ComponentProps<typeof IconButton> & {
  mediaType: MediaTypesType
  arpMediaIndex: number
  isDisabled?: boolean
  white?: boolean
  onFavorite?: () => void
  onUnfavorite?: () => void
}

const FavoriteButton: FC<FavoriteButtonProps> = observer(
  ({ mediaType, arpMediaIndex, isDisabled, white, css, onFavorite, onUnfavorite, ...props }) => {
    const { toggleFavorite, isFavorited } = useFavorite(arpMediaIndex, mediaType)
    const { user } = useAuth()

    return (
      <>
        {requiresUserLoggedIn(
          <FavoriteWithTooltip
            disabled={isDisabled}
            tooltipText={isFavorited ? 'Unfavorite' : 'Favorite'}
            isDisabled={isDisabled}
            css={{ marginLeft: '$8', color: white ? 'white' : 'black', ...css }}
            onClick={(e) => {
              if (!userIsLoggedIn(user)) return
              e.stopPropagation()
              toggleFavorite()
              if (isFavorited && onUnfavorite) onUnfavorite()
              if (!isFavorited && onFavorite) onFavorite()
            }}
            {...props}
          >
            {isFavorited ? <HeartFilled /> : <HeartOutlined />}
          </FavoriteWithTooltip>
        )}
      </>
    )
  }
)

const segmentFavorite = (media, button, location) => {
  window.analytics.track('Button Clicked', {
    button: button,
    sound: media.metadata.title,
    source: window.location.pathname,
    location: location,
    artistName: media.artist,
    artistEthAddress: media.artistEthAddress,
    genres: media.metadata.genres,
    bpm: media.metadata.bpm,
    tags: media.metadata.tags
  })
}

export const FavoriteArpMediaButton: FC<{ media: IArpMedia; css?: {}; location?: string }> = ({
  media,
  css,
  location
}) => (
  <FavoriteButton
    mediaType={media.type}
    arpMediaIndex={media.id}
    onFavorite={() => {
      segmentFavorite(media, 'favorite', location)
      media.updateFavoriteCount(media.favoriteCount + 1)
    }}
    onUnfavorite={() => {
      segmentFavorite(media, 'unfavorite', location)
      media.updateFavoriteCount(media.favoriteCount - 1)
    }}
    css={css}
  />
)

export default FavoriteButton
